import { autoinject, bindable, observable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { Models } from 'models/core';
import { ErrorService } from 'services/error-service';
import { LocationCodeService } from 'services/location-code-service';
import { PackagingTypeServiceService } from 'services/packaging-type-service-service';
import { ServiceProductService } from 'services/service-product-service';

type Model = {
  PackagingProductId: number | undefined;
  LocationCodeId: number | null | undefined;
  Amount: number | undefined;
  Id?: number;
};

type data = { data: ServiceConsumptionPackagingModel };

export type ServiceConsumptionPackagingModel = {
  ServiceId: number;
  PackagingProductId: number;
  LocationCodeId: number | null;
  Amount: number;
  Id?: number;
};

@autoinject
export class ServicePackagingConsumptionCreateEdit {
  @bindable
  @observable
  model?: Model;
  targetModel: Model = { PackagingProductId: 0, LocationCodeId: null, Amount: undefined };
  modelChanged(model: Model) {
    this.targetModel = JSON.parse(JSON.stringify(model));
    this.form.PackagingProductId = this.targetModel.PackagingProductId;
    this.form.LocationCodeId = this.targetModel.LocationCodeId;
    this.form.Amount = this.targetModel.Amount;
  }

  @bindable service!: Models.Service;

  @bindable onSave?: (data: data) => void | Promise<void>;
  @bindable onCanceled?: () => void;

  @bindable
  @observable
  protected visible = false;

  protected visibleChanged(newValue: boolean) {
    // When the form becomes visible in "create" mode (no existing model),
    // preselect the location code based on the service station's default
    // location code (if configured).
    if (newValue && !this.model) {
      this.applyDefaultLocationCode();
    }
  }

  private applyDefaultLocationCode() {
    const defaultLocationCodeId =
      this.service?.ServiceStation?.DefaultLocationCodeId;

    if (!defaultLocationCodeId) {
      return;
    }

    if (!this.form.LocationCodeId) {
      this.form.LocationCodeId = defaultLocationCodeId;
    }
  }

  private validationController: ValidationController;

  protected form: Model = {
    PackagingProductId: undefined,
    LocationCodeId: undefined,
    Amount: undefined,
  };

  constructor(
    private errorService: ErrorService,
    protected packagingTypeServiceService: PackagingTypeServiceService,
    private t: I18N,
    validationControllerFactory: ValidationControllerFactory,
    private serviceProductservice: ServiceProductService,
    private locationCodeService: LocationCodeService  
  ) {
    this.validationController = validationControllerFactory.createForCurrentScope();
  }

  protected applyValidationRules(item: Model) {
    ValidationRules.ensure('PackagingProductId')
      .required()
      .withMessage(this.t.tr('general.requiredField'))
      .ensure('Amount')
      .required()
      .withMessage(this.t.tr('general.requiredField'))
      .min(0)
      .withMessage(this.t.tr('validation.mustBeGreaterOrEqualToValue', { value: 0 }))
      .on(item);
  }

  private async validate(item: Model) {
    this.applyValidationRules(item);
    this.validationController.reset();
    return (await this.validationController.validate({ object: item })).valid;
  }

  protected async getPackagingTypesService() {
    const result = await this.serviceProductservice.getPackagingTypesService('Packaging', this.service.Id);
    return result.data;
  }

  private createModel() {
    const source = this.targetModel;
    const model: ServiceConsumptionPackagingModel = {
      ServiceId: this.service.Id,
      PackagingProductId: this.form.PackagingProductId!,
      LocationCodeId: this.form.LocationCodeId || null,
      Amount: this.form.Amount!,
    };

    if (source.Id) {
      model.Id = source.Id;
    }

    return model;
  }

  async save() {
    const model = this.createModel();

    if (!(await this.validate(model))) {
      return;
    }

    try {
      await this.onSave?.({
        data: this.createModel(),
      });
      this.resetForm();
    } catch (error) {
      this.errorService.handleError(error);
    }
  }

  resetForm() {
    this.form.PackagingProductId = 0;
    this.form.LocationCodeId = null;
    this.form.Amount = undefined;

    // Re-apply the service station's default location code so a subsequent
    // "create" starts with the preselected value again.
    this.applyDefaultLocationCode();
  }

  cancel() {
    this.onCanceled?.();
  }
}
